/**
 * Copyright 2025 ForgeRock AS. All Rights Reserved
 *
 * Use of this code requires a commercial software license with ForgeRock AS
 * or with one of its affiliates. All use shall be exclusively subject
 * to such license between the licensee and ForgeRock AS.
 */

/**
 * Generates a deterministic 16-character hash from an email address.
 *
 * @param {string} email - The email address to hash
 * @returns {string} A 16-character base-36 hash (using 0-9 and a-z)
 *
 * This function provides uniqueness by:
 * - Using independent hash calculations with different seeds (5381 and 52711)
 * - Incorporating character position in the second hash to differentiate anagrams
 *
 *
 * The resulting hash:
 * - Is always exactly 16 characters long
 * - Contains only alphanumeric characters (0-9, a-z)
 * - Is deterministic (same email always produces same hash)
 * - Has good distribution to minimize collisions
 * - Provides one-way privacy protection (can't reverse to get original email)
 *
 */
/* eslint-disable no-bitwise */
function emailHash(email) {
  const normalized = email.trim().toLowerCase();

  let h1 = 5381;
  let h2 = 52711;

  for (let i = 0; i < normalized.length; i += 1) {
    const ch = normalized.charCodeAt(i);
    h1 = ((h1 << 5) + h1) ^ ch;
    h2 = ((h2 << 5) + h2) ^ (ch + i);
  }

  const part1 = (h1 >>> 0).toString(36).padStart(8, '0');
  const part2 = (h2 >>> 0).toString(36).padStart(8, '0');

  return part1 + part2; // 16 base36 characters
}
/* eslint-enable no-bitwise */

export default emailHash;
